import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { SyncReviewPage, listSyncReviews } from '../../../../api/stays-sync-client';
import { CountBadge } from '../../../count-badge/count-badge';
import { useNotificationFrames } from '../../../notifications/use-notification-frames';
import { resolveClock } from '../../../../function-utils/building-time';
import { layout, palette, radius, shadow, spacing, transition, typography } from '../../../../style-utils/theme';
import { SyncBanners } from './sync-banners';

/**
 * THE STAYS PAGE'S CALENDAR HOOKS (plan S5, screens §3 + §4): a "Calendar
 * changes" tile with the count waiting (all buildings, or the chosen one) that
 * opens the review page, and — with a building chosen — that building's
 * banners (a failing calendar, imported stays with no guest name). Nothing is
 * drawn while there is nothing to say, or when the read fails (the page's own
 * content is what matters there).
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Tile = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  align-self: flex-start;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  text-decoration: none;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const SyncChangesTile: React.FC<{ buildingId: number | null }> = ({ buildingId }) => {
  const [page, setPage] = useState<SyncReviewPage | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    listSyncReviews({ state: 'pending', ...(buildingId ? { building_id: buildingId } : {}) })
      .then(out => {
        if (alive) setPage(out);
      })
      .catch(() => {
        if (alive) setPage(null);
      });

    return () => {
      alive = false;
    };
  }, [buildingId, tick]);

  useNotificationFrames(row => {
    if (row.event_type === 'stays.sync_review_needed') setTick(t => t + 1);
  });

  if (!page) return null;
  const pending = page.counts.pending || 0;
  const href = `/dashboard/stays/sync-review${buildingId ? `?building=${buildingId}` : ''}`;
  const clock = resolveClock({ surface: 'app', response: null });

  return (
    <Wrap data-testid="sync-changes-tile">
      {pending > 0 ? (
        <Tile to={href} aria-label={`Calendar changes, ${pending} to review`}>
          <span>Calendar changes</span>
          <CountBadge count={pending} tone="brand" />
        </Tile>
      ) : null}
      {buildingId ? (
        <SyncBanners
          scope="building"
          buildingId={buildingId}
          pending={0}
          failing={page.health.failing}
          guestNameRequired={page.health.guest_name_required}
          zone={clock.zone}
          now={new Date()}
        />
      ) : null}
    </Wrap>
  );
};
